import { ApiError } from './api';

/**
 * Turn a domain API error into copy a user can act on.
 *
 * Prefers the machine-readable `detail.error` code the backend attaches (see
 * `_domain_error_detail` in domain_router.py) and only falls back to matching error
 * prose for the endpoints that don't emit codes yet.
 */
export const friendlyDomainError = (err: any, domainName: string, fallback: string): string => {
  const code = err?.data?.detail?.error;
  switch (code) {
    case 'domain_finalizing':
      return `${domainName} is still finalizing its setup. It will be ready to assign in a few minutes.`;
    case 'domain_updating':
      return `${domainName} has a change in progress. Please wait for it to complete.`;
    case 'domain_already_assigned':
      return `${domainName} is already assigned to this project.`;
    case 'domain_setup_failed':
      return `${domainName} ran into a setup problem. Try Reconcile in Account Settings → Domains, or contact support.`;
    case 'domain_cooldown':
      return err instanceof ApiError ? err.message : fallback;
    case 'domain_assigned':
      return `${domainName} is assigned to a project. Unassign it there first, then delete it.`;
    case 'domain_not_byod':
      return `${domainName} was registered through Kolega and can't be deleted here. It stays active until it expires.`;
    case 'domain_already_deleting':
      return `${domainName} is already being deleted. This takes 15–20 minutes.`;
    case 'domain_deleting':
      return `${domainName} is being deleted and can no longer be changed.`;
    default:
      break;
  }

  const rawErrorMessage = err instanceof ApiError ? err.message : fallback;
  if (rawErrorMessage.includes('CloudFront distribution is currently being modified')) {
    return `${domainName} is still being set up. Please try again in a few minutes.`;
  }
  if (rawErrorMessage.includes('must have at least one successful deployment')) {
    return 'This project must be deployed at least once before custom domain assignment. Please use the Publish section to deploy your project first.';
  }
  return rawErrorMessage;
};
